interface StringTransformation {
  (input: string): string
}

/**
 * A composable string transformer that allows chaining string transformations
 * using functional composition. This exists to make transformation pipelines
 * more readable as opposed to nesting function calls:
 *
 * @example
 * ```ts
 * const transformer = new StringTransformer()
 *   .add(replaceDiacritics)
 *   .add(replaceWhiteSpace)
 *   .add(toLowerCase)
 *   .add(trim)
 *   .transform(input)
 * ```
 */
export class StringTransformer {
  constructor(private readonly transformations: StringTransformation[] = []) {}

  /**
   * Add a string transformation to the chain.
   * @returns The transformer instance for method chaining.
   */
  add(transformation: StringTransformation): StringTransformer {
    this.transformations.push(transformation)
    return this
  }

  /**
   * Apply all string transformations to the input string.
   * @returns The transformed string.
   */
  transform(input: string): string {
    let result = input

    for (const transformation of this.transformations) {
      result = transformation(result)
    }

    return result
  }
}
